import React, { useState, useEffect } from 'react';
import {
  Shield, AlertTriangle, Activity, TrendingDown, Users, Clock,
  Target, Loader2, ChevronRight, BarChart3, Layers
} from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';

const severityColor = { critical: 'bg-red-500', high: 'bg-orange-500', medium: 'bg-yellow-500', low: 'bg-blue-400' };
const statusColor = { open: 'bg-blue-500', investigating: 'bg-yellow-500', action_required: 'bg-red-500', closed: 'bg-green-500' };
const harmLabels = { no_harm: 'No Harm', near_miss: 'Near Miss', temporary_minor: 'Temp. Minor', temporary_major: 'Temp. Major', permanent_harm: 'Permanent', death: 'Death', not_classified: 'Not Classified' };

const SafetyIntelligencePage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/safety-analytics')
      .then(setData)
      .catch(e => toast.error(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-24" data-testid="safety-intel-loading"><Loader2 className="w-8 h-8 animate-spin text-[var(--niko-teal)]" /></div>;
  if (!data) return <div className="text-center py-24 text-gray-400" data-testid="safety-intel-error">Failed to load analytics</div>;

  const { summary, by_status, by_severity, by_category, by_department, by_harm_level, contributing_factors, monthly_trends, rca, corrective_actions } = data;
  const scoreColor = summary.safety_score >= 70 ? 'text-green-600' : summary.safety_score >= 40 ? 'text-yellow-600' : 'text-red-600';
  const scoreRingColor = summary.safety_score >= 70 ? 'stroke-green-500' : summary.safety_score >= 40 ? 'stroke-yellow-500' : 'stroke-red-500';

  const maxTrend = Math.max(...monthly_trends.map(m => m.total), 1);
  const factorEntries = Object.entries(contributing_factors).sort((a, b) => b[1] - a[1]);
  const maxFactor = factorEntries.length > 0 ? factorEntries[0][1] : 1;
  const categoryEntries = Object.entries(by_category).sort((a, b) => b[1] - a[1]);
  const deptEntries = Object.entries(by_department).sort((a, b) => b[1] - a[1]);
  const maxDept = deptEntries.length > 0 ? deptEntries[0][1] : 1;

  return (
    <div className="space-y-6" data-testid="safety-intelligence-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Safety Intelligence</h1>
        <p className="text-gray-500 mt-1">Advanced analytics, trends, and benchmarking for patient safety</p>
      </div>

      {/* Top Row: Safety Score + KPIs */}
      <div className="grid lg:grid-cols-5 gap-4">
        {/* Safety Score */}
        <div className="lg:col-span-1 bg-white rounded-2xl border border-gray-100 p-6 flex flex-col items-center justify-center" data-testid="safety-score-card">
          <div className="relative w-28 h-28">
            <svg className="w-28 h-28 -rotate-90" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="42" fill="none" strokeWidth="8" className="stroke-gray-100" />
              <circle cx="50" cy="50" r="42" fill="none" strokeWidth="8" className={scoreRingColor}
                strokeDasharray={`${(summary.safety_score / 100) * 264} 264`}
                strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className={`text-3xl font-bold ${scoreColor}`}>{summary.safety_score}</span>
            </div>
          </div>
          <p className="text-sm font-medium text-gray-700 mt-3">Safety Score</p>
          <p className="text-xs text-gray-400">out of 100</p>
        </div>

        {/* KPI Cards */}
        <div className="lg:col-span-4 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Total Events', value: summary.total_events, sub: `${summary.open_events} open`, icon: AlertTriangle, accent: 'text-orange-600 bg-orange-50' },
            { label: 'Patient Harm', value: summary.patient_harm, sub: `of ${summary.patient_involved} involved`, icon: Activity, accent: 'text-red-600 bg-red-50' },
            { label: 'Avg. Close Time', value: `${summary.avg_close_days}d`, sub: `${summary.closed_events} closed`, icon: Clock, accent: 'text-blue-600 bg-blue-50' },
            { label: 'Action Items', value: corrective_actions.total, sub: `${corrective_actions.overdue} overdue`, icon: Target, accent: corrective_actions.overdue > 0 ? 'text-red-600 bg-red-50' : 'text-green-600 bg-green-50' },
          ].map(m => (
            <div key={m.label} className="bg-white rounded-2xl border border-gray-100 p-5">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${m.accent} mb-3`}>
                <m.icon className="w-4.5 h-4.5" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{m.value}</p>
              <p className="text-sm text-gray-500">{m.label}</p>
              <p className="text-xs text-gray-400 mt-0.5">{m.sub}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Event Status + Severity + Harm Level */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Status Distribution */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Event Status Distribution</h2>
          <div className="flex h-4 rounded-full overflow-hidden mb-4">
            {Object.entries(by_status).filter(([,v]) => v > 0).map(([k, v]) => (
              <div key={k} className={`${statusColor[k]} transition-all`} style={{ width: `${(v / summary.total_events) * 100}%` }} title={`${k}: ${v}`} />
            ))}
          </div>
          <div className="space-y-2">
            {[
              { key: 'open', label: 'Open', color: 'bg-blue-500' },
              { key: 'investigating', label: 'Investigating', color: 'bg-yellow-500' },
              { key: 'action_required', label: 'Action Required', color: 'bg-red-500' },
              { key: 'closed', label: 'Closed', color: 'bg-green-500' },
            ].map(({ key, label, color }) => (
              <div key={key} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-2.5 h-2.5 rounded-full ${color}`} />
                  <span className="text-sm text-gray-600">{label}</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">{by_status[key] || 0}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Severity Breakdown */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Severity Breakdown</h2>
          <div className="space-y-3">
            {['critical', 'high', 'medium', 'low'].map(sev => {
              const count = by_severity[sev] || 0;
              const pct = summary.total_events > 0 ? (count / summary.total_events) * 100 : 0;
              return (
                <div key={sev}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700 capitalize">{sev}</span>
                    <span className="text-sm text-gray-500">{count} ({Math.round(pct)}%)</span>
                  </div>
                  <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full ${severityColor[sev]} rounded-full`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Harm Level */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Harm Classification (NCC MERP)</h2>
          <div className="space-y-2">
            {Object.entries(by_harm_level).sort((a, b) => b[1] - a[1]).map(([key, count]) => (
              <div key={key} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${
                    key === 'death' ? 'bg-black' :
                    key === 'permanent_harm' ? 'bg-red-600' :
                    key === 'temporary_major' ? 'bg-red-400' :
                    key === 'temporary_minor' ? 'bg-orange-400' :
                    key === 'near_miss' ? 'bg-yellow-400' :
                    key === 'no_harm' ? 'bg-green-400' : 'bg-gray-300'
                  }`} />
                  <span className="text-sm text-gray-600">{harmLabels[key] || key}</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Monthly Trend + Contributing Factors */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Monthly Trend (Bar chart via CSS) */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Monthly Event Trend (12 months)</h2>
          <div className="flex items-end gap-1 h-40" data-testid="monthly-trend-chart">
            {monthly_trends.map((m, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full flex flex-col items-center justify-end" style={{ height: '120px' }}>
                  {m.total > 0 && (
                    <div
                      className="w-full max-w-[28px] bg-gradient-to-t from-[var(--niko-teal)] to-[var(--niko-green)] rounded-t-md transition-all relative group"
                      style={{ height: `${(m.total / maxTrend) * 100}%`, minHeight: m.total > 0 ? '8px' : '0' }}
                    >
                      <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap">
                        {m.total} events
                      </div>
                      {m.critical > 0 && (
                        <div className="absolute bottom-0 left-0 right-0 bg-red-500 rounded-t-sm" style={{ height: `${(m.critical / m.total) * 100}%` }} />
                      )}
                    </div>
                  )}
                </div>
                <span className="text-[10px] text-gray-400 truncate w-full text-center">{m.month.split(' ')[0]}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-gradient-to-t from-[var(--niko-teal)] to-[var(--niko-green)]" /><span className="text-xs text-gray-500">Total</span></div>
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-red-500" /><span className="text-xs text-gray-500">Critical</span></div>
          </div>
        </div>

        {/* Contributing Factors */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Top Contributing Factors</h2>
          {factorEntries.length === 0 ? (
            <p className="text-gray-400 text-sm">No contributing factors recorded</p>
          ) : (
            <div className="space-y-3">
              {factorEntries.slice(0, 8).map(([factor, count], i) => (
                <div key={factor}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-700">{factor}</span>
                    <span className="text-sm font-medium text-gray-900">{count}</span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[var(--niko-teal)] to-[var(--niko-green)] rounded-full" style={{ width: `${(count / maxFactor) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Department Breakdown + Category Distribution + RCA/Action Summary */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* By Department */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Events by Department</h2>
          <div className="space-y-3">
            {deptEntries.map(([dept, count]) => (
              <div key={dept}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-700">{dept}</span>
                  <span className="text-sm font-semibold text-gray-900">{count}</span>
                </div>
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${(count / maxDept) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* By Category */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Events by Category</h2>
          <div className="space-y-2">
            {categoryEntries.map(([cat, count]) => (
              <div key={cat} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                <span className="text-sm text-gray-700">{cat}</span>
                <div className="flex items-center gap-2">
                  <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-[var(--niko-teal)] rounded-full" style={{ width: `${(count / summary.total_events) * 100}%` }} />
                  </div>
                  <span className="text-sm font-semibold text-gray-900 w-6 text-right">{count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* RCA & Actions Summary */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Investigations & Actions</h2>
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Root Cause Analyses</p>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div><p className="text-xl font-bold text-gray-900">{rca.total}</p><p className="text-xs text-gray-500">Total</p></div>
                <div><p className="text-xl font-bold text-green-600">{rca.completed}</p><p className="text-xs text-gray-500">Done</p></div>
                <div><p className="text-xl font-bold text-yellow-600">{rca.in_progress}</p><p className="text-xs text-gray-500">Active</p></div>
              </div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Corrective Actions</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: 'pending', label: 'Pending', color: 'text-yellow-600' },
                  { key: 'in_progress', label: 'In Progress', color: 'text-blue-600' },
                  { key: 'completed', label: 'Completed', color: 'text-green-600' },
                  { key: 'overdue', label: 'Overdue', color: 'text-red-600' },
                ].map(({ key, label, color }) => (
                  <div key={key} className="text-center p-1">
                    <p className={`text-lg font-bold ${color}`}>{corrective_actions.by_status[key] || 0}</p>
                    <p className="text-xs text-gray-500">{label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SafetyIntelligencePage;
